import { app } from '../app/app.js';
import { assert } from '../app/logger.js';
import { UIBlocker } from '../ui/ui_blocker.js';
import { run } from '../ui/ui_queueable.js';
import { window } from '../window.js';

/**
 * Adds event listener to the given element. When event is fired the listener is
 * queued. Supported options:
 *   * blockUI - blocks UI until the listener is fullfilled
 *   * preventDefault - event.preventDefault() is called
 *   * capture - the listener will trigger on capture phase.
 */
function listenToEvent(elementOrElements, eventType, listener, options = {}) {
  assert(
    elementOrElements &&
      (elementOrElements instanceof window.Node ||
        Symbol.iterator in elementOrElements),
    `listenToEvent: wrong elementOrElements are given, got ${elementOrElements}`
  );

  let eventListener = ev => {
    if (options.preventDefault) {
      ev.preventDefault();
    }
    if (options.stopPropagation) {
      ev.stopPropagation();
    }

    let authWrapper = options.authenticate
      ? () => app.authcall(() => listener(ev))
      : () => listener(ev);

    let uiWrapper = () =>
      options.blockUI
        ? UIBlocker(Promise.resolve(authWrapper()))
        : authWrapper(ev);

    options.sync ? uiWrapper() : run(uiWrapper);
  };

  let eventArgs = [
    eventType,
    eventListener,
    {
      capture: options.capture,
    },
  ];
  let elements =
    elementOrElements instanceof window.Node
      ? [elementOrElements]
      : [...elementOrElements];
  elements.forEach(element => element.addEventListener(...eventArgs));

  return {
    remove() {
      elements.forEach(element => element.removeEventListener(...eventArgs));
    },
  };
}

function listenToEventSync(
  elementOrElements,
  eventType,
  listener,
  options = {}
) {
  return listenToEvent(
    elementOrElements,
    eventType,
    listener,
    Object.assign({ sync: true }, options)
  );
}

export { listenToEvent, listenToEventSync };
